import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type * as React from "react";
import cn from "twsx";

// Figma type scale (Linear measured): 11/16 micro, 12/16 small and meta,
// 13/20 body, 15/24 large. Weights 400/500/600. Tones are the text tokens.
export const textVariants = cva("", {
  variants: {
    size: {
      xs: "text-xs leading-4",
      sm: "text-sm leading-4",
      md: "text-base leading-5",
      lg: "text-lg leading-6",
      inherit: ""
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      inherit: ""
    },
    tone: {
      primary: "text-text-primary",
      secondary: "text-text-secondary",
      tertiary: "text-text-tertiary",
      quaternary: "text-text-quaternary",
      danger: "text-red-text",
      success: "text-green-text",
      warning: "text-yellow-text",
      inverse: "text-text-on-accent",
      inherit: ""
    },
    truncate: {
      true: "min-w-0 truncate",
      false: ""
    },
    numeric: {
      true: "tabular-nums",
      false: ""
    }
  },
  defaultVariants: {
    size: "md",
    weight: "normal",
    tone: "primary",
    truncate: false,
    numeric: false
  }
});
type TextElement = "span" | "p" | "div" | "dt" | "dd" | "label" | "strong";
export type TextProps = VariantProps<typeof textVariants> & Omit<React.HTMLAttributes<HTMLElement>, "color"> & {
  as?: TextElement;
  htmlFor?: string;
};

/** Use when rendering body text, labels and metadata on the type scale. */
export function Text(t0) {
  const $ = _c(20);
  let className;
  let numeric;
  let props;
  let size;
  let t1;
  let tone;
  let truncate;
  let weight;
  if ($[0] !== t0) {
    ({
      as: t1,
      size,
      weight,
      tone,
      truncate,
      numeric,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = numeric;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = tone;
    $[7] = truncate;
    $[8] = weight;
  } else {
    className = $[1];
    numeric = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    tone = $[6];
    truncate = $[7];
    weight = $[8];
  }
  const Element = t1 === undefined ? "span" : t1;
  let t2;
  if ($[9] !== className || $[10] !== numeric || $[11] !== size || $[12] !== tone || $[13] !== truncate || $[14] !== weight) {
    t2 = cn(textVariants({
      size,
      weight,
      tone,
      truncate,
      numeric
    }), className);
    $[9] = className;
    $[10] = numeric;
    $[11] = size;
    $[12] = tone;
    $[13] = truncate;
    $[14] = weight;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== Element || $[17] !== props || $[18] !== t2) {
    t3 = <Element className={t2} {...props} />;
    $[16] = Element;
    $[17] = props;
    $[18] = t2;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  return t3;
}
export default Text;